const BASE_KEY = "AesGcmHelper Secret API Info Key";
const NONCE_LENGTH = 12;
const TAG_LENGTH = 16;
const KEY_LENGTH = 32;

function getKeyBytes(key: string): Uint8Array {
  const encoder = new TextEncoder();
  const bytes = encoder.encode(`${key} ${BASE_KEY}`);
  const result = new Uint8Array(KEY_LENGTH);
  result.set(bytes.slice(0, KEY_LENGTH));
  return result;
}

export async function encrypt(key: string, value: string): Promise<string> {
  const keyBytes = getKeyBytes(key);
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyBytes.buffer as ArrayBuffer,
    "AES-GCM",
    false,
    ["encrypt"],
  );

  const nonce = crypto.getRandomValues(new Uint8Array(NONCE_LENGTH));
  const plaintext = new TextEncoder().encode(value);

  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce, tagLength: TAG_LENGTH * 8 },
    cryptoKey,
    plaintext,
  );

  const encryptedArray = new Uint8Array(encrypted);
  const ciphertext = encryptedArray.slice(0, encryptedArray.length - TAG_LENGTH);
  const tag = encryptedArray.slice(encryptedArray.length - TAG_LENGTH);

  // C# 포맷: nonce(12) + tag(16) + ciphertext
  const result = new Uint8Array(NONCE_LENGTH + TAG_LENGTH + ciphertext.length);
  result.set(nonce, 0);
  result.set(tag, NONCE_LENGTH);
  result.set(ciphertext, NONCE_LENGTH + TAG_LENGTH);

  return btoa(String.fromCharCode(...result));
}

export async function decrypt(key: string, value: string): Promise<string> {
  const keyBytes = getKeyBytes(key);
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyBytes.buffer as ArrayBuffer,
    "AES-GCM",
    false,
    ["decrypt"],
  );

  const raw = Uint8Array.from(atob(value), (c) => c.charCodeAt(0));
  const nonce = raw.slice(0, NONCE_LENGTH);
  const tag = raw.slice(NONCE_LENGTH, NONCE_LENGTH + TAG_LENGTH);
  const ciphertext = raw.slice(NONCE_LENGTH + TAG_LENGTH);

  // Web Crypto expects ciphertext + tag appended
  const combined = new Uint8Array(ciphertext.length + TAG_LENGTH);
  combined.set(ciphertext, 0);
  combined.set(tag, ciphertext.length);

  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: nonce, tagLength: TAG_LENGTH * 8 },
    cryptoKey,
    combined,
  );

  return new TextDecoder().decode(decrypted);
}
